import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faInfoCircle } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import Checkbox from '@/components/ui/Checkbox';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast } from '@/utils/toasts.ts';
import type { LongTermRecommendation, LongTermType } from '@/models';

interface EditLongTermRecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  longTermTrade: LongTermRecommendation | null;
  onLongTermRecommendationUpdated: (longTermTrade: LongTermRecommendation) => void;
}

export default function EditLongTermRecommendationModal({
  isOpen,
  onClose,
  longTermTrade,
  onLongTermRecommendationUpdated
}: EditLongTermRecommendationModalProps) {
  const [type, setType] = useState<LongTermType>('LONG');
  const [boost, setBoost] = useState(false);
  const [free, setFree] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (longTermTrade) {
      setType(longTermTrade.type);
      setBoost(longTermTrade.boost);
      setFree(longTermTrade.free);
    }
  }, [longTermTrade]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!longTermTrade) return;

    setLoading(true);

    try {
      const requestData = {
        type: type,
        boost: boost,
        free: free
      };

      const updatedLongTermRecommendation = await APIRoutes.updateLongTermTrade(longTermTrade.id, requestData);
      onLongTermRecommendationUpdated(updatedLongTermRecommendation);
      showSuccessToast('Langzeit Trade erfolgreich aktualisiert');
      onClose();
    } catch (error) {
      handleAPIError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!longTermTrade) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Langzeit Trade bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-purple-600" />}
      maxWidth="md"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
            />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Eingeschränkte Bearbeitungsmöglichkeiten</p>
              <p>
                Nur die Handelsrichtung kann hier geändert werden. Verwenden Sie <strong>Adjustments</strong>,
                um Positionen anzupassen, oder <strong>Trade beenden</strong>, um den Trade abzuschließen.
              </p>
            </div>
          </div>
        </div>

        {/* Trade Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Handelsrichtung
          </label>
          <select
            id="type"
            value={type}
            disabled={true}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
          >
            <option value="LONG">Long</option>
          </select>
        </div>

        <Checkbox
          checked={boost}
          onChange={setBoost}
          disabled={loading}
          colorTheme="purple"
          label="Als Boost-Trade markieren"
        />

        <Checkbox
          checked={free}
          onChange={setFree}
          disabled={loading}
          colorTheme="purple"
          label="Als kostenlosen Trade markieren"
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
